import {
    Activity,
    ArrowUpRight,
    Box,
    CalendarDays,
    CheckCircle2,
    Clock,
    Copy,
    MapPin,
    RefreshCw,
    Truck
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../../hooks/useToast';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../ui/table';

interface TrackingStats {
  statusStats: Array<{
    _id: string;
    count: number;
    totalWeight: number;
    totalValue: number;
  }>;
  recentEvents: Array<{
    awbNumber: string;
    eventDate: string; // API returns string date
    eventDescription: string;
    eventLocation: string;
    eventState: string;
    createdBy?: {
      name: string;
    }
  }>;
}

interface TrackingOverviewProps {
  onNavigateToShipment: (awb: string) => void;
}

import { useAuth } from '../../../hooks/useAuth';

export const TrackingOverview: React.FC<TrackingOverviewProps> = ({ onNavigateToShipment }) => {
  const [stats, setStats] = useState<TrackingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const { toast } = useToast();
  const { axios } = useAuth();

  const fetchStats = useCallback(async (mode: 'initial' | 'refresh' = 'initial') => {
    try {
      if (mode === 'initial') {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      const response = await axios.get('/api/tracking/stats');
      const data = response.data;
      if (data.success) {
        setStats(data.data);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast({
        title: "Error",
        description: "Failed to load dashboard data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [axios, toast]);

  useEffect(() => {
    fetchStats('initial');
  }, [fetchStats]);

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      'DRAFT': 'bg-slate-500',
      'BOOKED': 'bg-blue-600',
      'PICKED_UP': 'bg-amber-600',
      'IN_TRANSIT': 'bg-indigo-600',
      'OUT_FOR_DELIVERY': 'bg-purple-600',
      'DELIVERED': 'bg-emerald-600',
      'RETURNED': 'bg-red-600',
      'CANCELLED': 'bg-slate-700',
    };
    return statusColors[status] || 'bg-slate-500';
  };

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
    [],
  );

  const formatEventDate = useCallback((value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return dateFormatter.format(date);
  }, [dateFormatter]);

  const recentEvents = useMemo(() => {
    if (!stats?.recentEvents) return [];
    return stats.recentEvents.map((event) => ({
      ...event,
      statusLabel: event.eventState.replace(/_/g, ' '),
      eventDateLabel: formatEventDate(event.eventDate),
    }));
  }, [formatEventDate, stats]);

  const { totalShipments, deliveredCount, inTransitCount, outForDeliveryCount } = useMemo(() => {
    const statusStats = stats?.statusStats ?? [];
    return {
      totalShipments: statusStats.reduce((acc, curr) => acc + curr.count, 0),
      deliveredCount: statusStats.find(s => s._id === 'DELIVERED')?.count || 0,
      inTransitCount: statusStats.find(s => s._id === 'IN_TRANSIT')?.count || 0,
      outForDeliveryCount: statusStats.find(s => s._id === 'OUT_FOR_DELIVERY')?.count || 0,
    };
  }, [stats]);

  const handleCopyAwb = useCallback(async (awbNumber: string) => {
    try {
      await navigator.clipboard.writeText(awbNumber);
      toast({
        title: 'Copied',
        description: `AWB ${awbNumber} copied to clipboard.`,
      });
    } catch (error) {
      console.error('Failed to copy AWB:', error);
      toast({
        title: 'Copy failed',
        description: 'Unable to copy the AWB number. Please try again.',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const isInitialLoading = loading && !stats;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/20 dark:to-slate-900 border-blue-100 dark:border-blue-900">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Shipments</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{totalShipments}</h3>
                </div>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Box className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/20 dark:to-slate-900 border-indigo-100 dark:border-indigo-900">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">In Transit</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{inTransitCount}</h3>
                </div>
              </div>
              <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <Truck className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-white dark:from-purple-950/20 dark:to-slate-900 border-purple-100 dark:border-purple-900">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Out for Delivery</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{outForDeliveryCount}</h3>
                </div>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Activity className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/20 dark:to-slate-900 border-emerald-100 dark:border-emerald-900">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Delivered</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{deliveredCount}</h3>
                </div>
              </div>
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Latest Activity Feed */}
      <Card className="border shadow-sm">
        <CardHeader className="border-b bg-slate-50/50 dark:bg-slate-900/50 px-6 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Clock className="h-5 w-5 text-slate-500" />
                Live Tracking Feed
              </CardTitle>
              <CardDescription>
                Real-time updates of the latest tracking events across all shipments
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {lastUpdated && (
                <span className="text-xs text-slate-500">
                  Updated {dateFormatter.format(lastUpdated)}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchStats('refresh')}
                disabled={isRefreshing}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh Feed'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="md:hidden p-4 space-y-3">
            {isInitialLoading ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                Loading recent activity...
              </div>
            ) : recentEvents.length > 0 ? (
              recentEvents.map((event, idx) => (
                <div key={idx} className="rounded-lg border bg-background p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <Badge variant="outline" className="font-mono">
                        {event.awbNumber}
                      </Badge>
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {event.eventDescription}
                      </div>
                      {event.createdBy?.name && (
                        <div className="text-xs text-slate-500">by {event.createdBy.name}</div>
                      )}
                    </div>
                    <Badge className={`${getStatusColor(event.eventState)} text-white border-0 shadow-none font-normal`}>
                      {event.statusLabel}
                    </Badge>
                  </div>
                  <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5" />
                      <span>{event.eventLocation || 'Unknown location'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-3.5 w-3.5" />
                      <span>{event.eventDateLabel}</span>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-2 sm:grid-cols-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => handleCopyAwb(event.awbNumber)}
                    >
                      <Copy className="mr-2 h-3.5 w-3.5" />
                      Copy AWB
                    </Button>
                    <Button
                      size="sm"
                      className="w-full"
                      onClick={() => onNavigateToShipment(event.awbNumber)}
                    >
                      <ArrowUpRight className="mr-2 h-3.5 w-3.5" />
                      View Details
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                No recent tracking events found.
              </div>
            )}
          </div>
          <div className="hidden md:block">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="px-6">AWB Number</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead className="text-right px-6">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isInitialLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">Loading recent activity...</TableCell>
                    </TableRow>
                  ) : recentEvents.length > 0 ? (
                    recentEvents.map((event, idx) => (
                      <TableRow key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors">
                        <TableCell className="font-medium px-6">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm">{event.awbNumber}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-slate-900 dark:text-slate-100">{event.eventDescription}</div>
                          {event.createdBy?.name && (
                            <div className="text-xs text-slate-500">by {event.createdBy.name}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 text-slate-500">
                            <MapPin className="h-3.5 w-3.5" />
                            {event.eventLocation}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusColor(event.eventState)} text-white border-0 shadow-none font-normal`}>
                            {event.statusLabel}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                            <CalendarDays className="h-3.5 w-3.5" />
                            {event.eventDateLabel}
                          </div>
                        </TableCell>
                        <TableCell className="text-right px-6">
                          <div className="inline-flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleCopyAwb(event.awbNumber)}
                              aria-label={`Copy AWB ${event.awbNumber}`}
                            >
                              <Copy className="h-4 w-4 text-slate-500 hover:text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => onNavigateToShipment(event.awbNumber)}
                              aria-label={`View AWB ${event.awbNumber}`}
                            >
                              <ArrowUpRight className="h-4 w-4 text-slate-500 hover:text-blue-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">No recent tracking events found.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
